/*
 * team-selector.js
 * Ensures a team is selected (auto-select first team if none)
*/

import teamApi from '@/api/team'
import store from '@/store'

export async function setSelectedTeam(team) {
    if (!team) return

    const member = team.members.find(m => m.employee.userId === store.state.userId)
    const role = member ? member.role : ''
    
    store.commit("setSelectedTeamName", team.name)
    store.commit("setSelectedTeamCompanyId", team.companyId)
    store.commit("setSelectedTeamId", team.teamId)
    store.commit("setSelectedTeamRole", role)
    store.commit("setSelectedTeamPermissions", team.permissions || [])
}

/**
 * Synchronizes the selected team based on a specific userId (requestor) 
 * or falls back to the first available team.
 */
export async function syncTeamContext(requestorUserId = null) {
    try {
        const selectedTeamId = store.state.selectedTeamId
        const selectedTeamCompanyId = store.state.selectedTeamCompanyId
        const loggedInCompanyId = store.state.companyId

        const response = await teamApi.getMyTeams()
        if (!response || response.status !== 200) return null
        
        const teams = response.data.myTeams
        if (!teams.length) return null

        if (requestorUserId) {
            const matchingTeams = teams.filter(team => 
                team.members.some(member => member.employee.userId === requestorUserId)
            )

            // ✅ NEW LOGIC: Check if current selection is ALREADY valid for this requestor
            const isAlreadyOnCorrectTeam = matchingTeams.some(t => t.teamId === selectedTeamId)
            if (isAlreadyOnCorrectTeam) {
                // The reviewer is already on one of the requestor's teams. Do nothing.
                return selectedTeamId
            }

            // IF MULTIPLE: Only show dialog if current selection ISN'T one of them
            if (matchingTeams.length > 1) {
                return { action: 'SHOW_DIALOG', teams: matchingTeams }
            }

            // IF ONE: Auto-switch to it
            if (matchingTeams.length === 1) {
                if (matchingTeams[0].teamId !== selectedTeamId) {
                    setSelectedTeam(matchingTeams[0])
                }
                return matchingTeams[0].teamId
            }

            // Fallback if requestor has no teams
            if (matchingTeams.length === 0) {
                // If current selection is valid for company, stay there, otherwise pick first
                if (selectedTeamId && selectedTeamCompanyId === loggedInCompanyId) {
                    return selectedTeamId
                }
                setSelectedTeam(teams[0])
                return teams[0].teamId
            }
        }
        
        // Priority 2: Fallback for non-requestor flows
        if (selectedTeamId && selectedTeamCompanyId === loggedInCompanyId) {
            return selectedTeamId
        }

        setSelectedTeam(teams[0])
        return teams[0].teamId

    } catch (error) {
        console.error("Failed to sync team context:", error)
        return null
    }
}

export async function refreshMyTeams() {
    try {
        const response = await teamApi.getMyTeams()
        if (!response || response.status !== 200) return null
        
        const teams = response.data.myTeams
        const selectedTeam = teams.find(x => x.teamId == store.state.selectedTeamId)

        if (selectedTeam) {
            setSelectedTeam(selectedTeam)
        }

    } catch (error) {
        console.error("Failed to refresh MyTeams:", error)
        return null
    }
}

export async function getDefaultTeamId() {
    // 1. Return existing selection if present
    if (store.state.selectedTeamId) {
        return store.state.selectedTeamId
    }

    try {
        // 2. Fetch teams if no local selection exists
        const response = await teamApi.getMyTeams()
        if (!response || response.status !== 200) return null

        const teams = response.data.myTeams
        if (!teams || !teams.length) return null

        // 3. set selected team
        setSelectedTeam(teams[0])

        // 4. Return the ID of the first team record
        return teams[0].teamId
    } catch (error) {
        console.error("Failed to get default team ID:", error)
        return null
    }
}